import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/admin");
  }, [user, loading, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Bienvenido de nuevo");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#08080a] px-6">
      <div className="pointer-events-none absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-[#ff6b00] opacity-[0.08] blur-[140px]" />
      <Link
        to="/"
        className="absolute left-6 top-6 flex items-center gap-2 font-mono-jb text-xs uppercase tracking-[0.18em] text-zinc-500 transition-colors hover:text-zinc-200"
        data-testid="login-back"
      >
        <ArrowLeft size={15} /> Volver
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0e0e11] p-9"
      >
        <div className="flex justify-center">
          <Logo size={48} />
        </div>
        <h1 className="mt-8 text-center font-display text-3xl font-extrabold tracking-tight text-zinc-100">
          Panel de administración
        </h1>
        <p className="mt-2 text-center text-sm text-zinc-500">
          Ingresá para editar el contenido del sitio.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <label className="font-mono-jb text-[10px] uppercase tracking-[0.18em] text-zinc-500">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-zinc-100 outline-none transition-colors focus:border-[#ff6b00]"
              placeholder="admin@aurex.com"
            />
          </div>
          <div>
            <label className="font-mono-jb text-[10px] uppercase tracking-[0.18em] text-zinc-500">Contraseña</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-zinc-100 outline-none transition-colors focus:border-[#ff6b00]"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl copper-bg py-3.5 text-sm font-semibold text-[#0a0a0c] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            Ingresar
          </button>
        </form>
      </motion.div>
    </div>
  );
}
